import React, { useMemo, useState } from 'react';

import { CartesianGrid, Tooltip as ChartTooltip, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

import { useGetChainLpcLppSupplierDailyTrend } from '~/hooks/apis/price-parity/useGetChainLpcLppSupplierDailyTrend';

import type { ChainLpcLppSupplierPropertyGroup } from '~/types/services/priceParrot';

import { fmtShortDateWithYear } from '../PriceParrotParityTool/utils';
import TogglePillGroup from '../shared/TogglePillGroup';

import { TrendGranularity, bucketDailyTrend } from './trendBucketing';
import { errMsg } from './utils';

interface Props {
  group: ChainLpcLppSupplierPropertyGroup | null;
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}

const GRANULARITY_OPTIONS: ReadonlyArray<{ value: TrendGranularity; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

export default function HotelRatioTrendModal({ group, dateFrom, dateTo, onClose }: Props) {
  const open = group != null;
  const [granularity, setGranularity] = useState<TrendGranularity>('weekly');

  const { data, isFetching, isError, error } = useGetChainLpcLppSupplierDailyTrend(
    group?.chain_name ?? undefined,
    group?.offer_bk,
    dateFrom,
    dateTo,
    open,
  );

  const rows = useMemo(() => bucketDailyTrend(data?.daily ?? [], granularity), [data?.daily, granularity]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
          {group?.property_name ?? group?.offer_bk} — LPC:LPP ratio over time
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load ratio trend: {errMsg(error)}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <TogglePillGroup<TrendGranularity>
            label="Granularity"
            ariaLabel="Chart granularity"
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={setGranularity}
          />
        </Box>

        <Box sx={{ position: 'relative', minHeight: 320 }}>
          {isFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}
          {!isFetching && !isError && rows.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              No daily data for this property in the selected range.
            </Typography>
          )}
          {!isFetching && rows.length > 0 && (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={rows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="bucket"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  tickFormatter={(v) => fmtShortDateWithYear(String(v))}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <ChartTooltip
                  labelFormatter={(label) => fmtShortDateWithYear(String(label))}
                  formatter={(value: number | string) =>
                    value == null || value === '' ? '—' : Number(value).toFixed(2)
                  }
                />
                <Line
                  type="monotone"
                  dataKey="ratio"
                  name="LPC:LPP ratio"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
